import { User, Target, MessageCircle, BarChart3 } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';
import type { PackageConfig } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';

interface Props {
 config: PackageConfig;
 buyerProxy: BuyerProxyResult;
}

function QuoteList({ quotes, max = 3 }: { quotes: string[]; max?: number }) {
 return (
 <div className="space-y-1.5">
 {quotes.slice(0, max).map((q, i) => (
 <p key={i} className="text-sm text-secondary border-l-2 border-chart-teal/30 pl-2 italic leading-relaxed">
 "{q.length > 120 ? q.slice(0, 117) + '...' : q}"
 </p>
 ))}
 </div>
 );
}

export function AudiencePersonaCard({ config, buyerProxy }: Props) {
 const d = buyerProxy.demographics;
 const b = buyerProxy.conjointBehavior;

 const tierLabel = config.price >= 1499 ? 'Premium' : config.price >= 999 ? 'Core' : 'Starter';

 return (
 <div className="card mb-6 border-l-4 border-l-chart-teal">
 <div className="flex items-center gap-2 mb-4">
 <User size={16} className="text-chart-teal" />
 <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">
 Your {tierLabel} Buyer — <span className="font-mono">{buyerProxy.count}</span> respondent proxy
 </h3>
 <InfoButton helpKey="groupProfile" size={13} />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {/* Demographics */}
 <div>
 <div className="flex items-center gap-1.5 mb-2">
 <Target size={13} className="text-muted" />
 <span className="text-xs font-semibold uppercase tracking-wider text-muted">Demographics</span>
 </div>
 <div className="space-y-1.5">
 <PersonaStat label="Age" value={<><span className="font-mono">{d.ageRange[0]}–{d.ageRange[1]}</span> <span className="text-muted">(median <span className="font-mono">{d.medianAge}</span>)</span></>} />
 <PersonaStat label="Gender" value={<><span className="font-mono">{d.femalePercent}%</span> Female · <span className="font-mono">{d.malePercent}%</span> Male</>} />
 {d.topIncomes.length > 0 && (
 <PersonaStat label="Income" value={<>{d.topIncomes[0].label} <span className="text-muted font-mono">({d.topIncomes[0].percent}%)</span></>} />
 )}
 {d.topRegions.length > 0 && (
 <PersonaStat label="Regions" value={d.topRegions.map(r => <span key={r.label}>{r.label} <span className="font-mono text-muted">({r.percent}%)</span></span>).reduce((prev, curr, i) => <>{prev}{i > 0 ? ', ' : ''}{curr}</> as any, <></> as any)} />
 )}
 </div>
 </div>

 {/* Conjoint Behavior */}
 <div>
 <div className="flex items-center gap-1.5 mb-2">
 <BarChart3 size={13} className="text-muted" />
 <span className="text-xs font-semibold uppercase tracking-wider text-muted">Conjoint Behavior</span>
 </div>
 <div className="space-y-1.5">
 <PersonaStat label="Products Chosen" value={<><span className="font-mono">{(15 - b.avgNoneRate).toFixed(1)}</span><span className="text-muted">/15 tasks</span></>} />
 {b.avgChosenPrice !== null && (
 <PersonaStat label="Avg Price" value={<span className="font-mono">${b.avgChosenPrice.toLocaleString()}</span>} />
 )}
 <PersonaStat label="Top Features" value={b.topFeatures.slice(0, 3).map(f =>
 `${f.name.split(' ')[0]} (${Math.round(f.rate * 100)}%)`
 ).join(', ')} />
 </div>
 </div>

 {/* What They Want */}
 <div>
 <div className="flex items-center gap-1.5 mb-2">
 <MessageCircle size={13} className="text-muted" />
 <span className="text-xs font-semibold uppercase tracking-wider text-muted">What They Want (Q1 Goals)</span>
 </div>
 {d.topGoals.length > 0 ? (
 <>
 <div className="flex flex-wrap gap-1.5 mb-2">
 {d.topGoals.map(g => (
 <span key={g.theme} className="px-2 py-0.5 rounded-full text-micro font-medium bg-chart-teal/10 text-chart-teal">
 {g.label} ({g.percentage}%)
 </span>
 ))}
 </div>
 <QuoteList quotes={d.topGoals[0]?.sampleQuotes || []} />
 </>
 ) : (
 <p className="text-xs text-muted italic">No goal themes extracted</p>
 )}
 </div>

 {/* What Would Make Them Pay */}
 <div>
 <div className="flex items-center gap-1.5 mb-2">
 <MessageCircle size={13} className="text-muted" />
 <span className="text-xs font-semibold uppercase tracking-wider text-muted">What Would Make Them Pay (Q8)</span>
 </div>
 {d.topWtpFactors.length > 0 ? (
 <>
 <div className="flex flex-wrap gap-1.5 mb-2">
 {d.topWtpFactors.map(f => (
 <span key={f.theme} className="px-2 py-0.5 rounded-full text-micro font-medium bg-chart-amber/15 text-chart-amber">
 {f.label} ({f.percentage}%)
 </span>
 ))}
 </div>
 <QuoteList quotes={d.topWtpFactors[0]?.sampleQuotes || []} />
 </>
 ) : (
 <p className="text-xs text-muted italic">No WTP themes extracted</p>
 )}
 </div>
 </div>

 {/* Brand Awareness */}
 <div className="mt-4 pt-4 border-t border-border">
 <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">AlterMe Brand Perception (Q12)</div>
 <div className="flex flex-wrap gap-4 text-sm text-secondary">
 <span><span className="font-mono font-medium text-primary">{d.brandAwareness.neverHeardPercent}%</span> never heard of it</span>
 <span><span className="font-mono font-medium text-primary">{d.brandAwareness.positivePercent}%</span> positive associations</span>
 <span><span className="font-mono font-medium text-primary">{d.brandAwareness.negativePercent}%</span> negative associations</span>
 </div>
 </div>

 <p className="text-micro text-muted mt-3 italic">
 Audience profile derived from respondents whose conjoint behavior aligns with this package's price and features. Directional estimate, not a definitive buyer segment.
 </p>
 </div>
 );
}

function PersonaStat({ label, value }: { label: string; value: React.ReactNode }) {
 return (
 <div className="flex items-baseline gap-2 text-xs">
 <span className="text-muted shrink-0 w-24">{label}</span>
 <span className="text-primary font-medium">{value}</span>
 </div>
 );
}
